import React from "react";
import { useInView } from "react-intersection-observer";
import { Building2, User } from "lucide-react";

interface CompanyProps {
  mode?: "about" | "inline";
}

const Company: React.FC<CompanyProps> = ({ mode = "inline" }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const companyInfo = [
    { label: "社名", value: "株式会社Senrigan" },
    { label: "設立", value: "2000年8月" },
    { label: "資本金", value: "1000万円" },
    { label: "所在地", value: "広島県広島市安佐南区山本2-3-35" },
    { label: "代表者", value: "宮川 清実" },
    {
      label: "事業内容",
      value: "Webシステム受託開発、LP・Webサイト制作、業務アプリ開発、システム保守",
    },
  ];

  const containerClass = mode === "about" ? "min-h-screen py-32" : "py-20 lg:py-28";

  return (
    <section id="company" className={`${containerClass} bg-white`}>
      <div className="mx-auto max-w-6xl px-5 lg:px-6">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
              <span className="text-sm font-semibold text-[#B8860B]">会社について</span>
            </div>
            <h2 className="text-3xl font-bold lg:text-4xl leading-tight text-[#2D3748]">
              1人 + AIの<br />
              <span className="text-gradient">新しい開発スタイル</span>
            </h2>
          </div>

          {/* 信頼の実績 */}
          <div className="grid grid-cols-3 gap-4 mb-10">
            <div className="rounded-2xl border border-[#E8E6E1] bg-white p-5 text-center shadow-sm">
              <div className="text-3xl lg:text-4xl font-bold text-[#1B2A4A]">26<span className="text-lg">年</span></div>
              <div className="text-xs text-[#7B8794] mt-1">設立からの実績</div>
            </div>
            <div className="rounded-2xl border border-[#E8E6E1] bg-white p-5 text-center shadow-sm">
              <div className="text-3xl lg:text-4xl font-bold text-[#1B2A4A]">1,000<span className="text-lg">万円</span></div>
              <div className="text-xs text-[#7B8794] mt-1">資本金</div>
            </div>
            <div className="rounded-2xl border border-[#E8E6E1] bg-white p-5 text-center shadow-sm">
              <div className="text-3xl lg:text-4xl font-bold text-[#1B2A4A]">1<span className="text-lg">人</span></div>
              <div className="text-xs text-[#7B8794] mt-1">代表が直接対応</div>
            </div>
          </div>

          {/* 代表が直接開発 カード */}
          <div className="rounded-2xl bg-[#1B2A4A] p-8 lg:p-10 mb-10">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* 写真 */}
              <div className="flex-shrink-0 flex justify-center lg:justify-start">
                <img
                  src="/images/miyagawa.png"
                  alt="代表 宮川 清実"
                  className="w-32 h-32 lg:w-40 lg:h-40 rounded-2xl object-cover border-2 border-[#B8860B]/30"
                />
              </div>
              {/* テキスト */}
              <div className="flex-1">
                <h3 className="text-xl lg:text-2xl font-bold text-[#F5F3EF] mb-5">代表が直接、開発します</h3>
                <div className="text-[#F5F3EF]/80 leading-relaxed space-y-4 text-justify">
                  <p>
                    株式会社Senriganは、代表の宮川がお客様のヒアリングから設計・開発・納品まで一貫して担当します。
                  </p>
                  <p>
                    最新のAI技術（Claude Code等）を活用することで、従来10人のチームで行っていた作業を1人で実現。人件費の大幅なカットが、低価格の秘密です。
                  </p>
                  <p>
                    大きなIT企業に依頼すると、あなたの声は営業→PM→SE→PGと伝言ゲームで薄まります。Senriganなら、<span className="text-[#B8860B] font-semibold">作る人に直接話せます。</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 会社概要 */}
          <div className="rounded-2xl border border-[#E8E6E1] bg-white p-8 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1B2A4A]/10">
                <Building2 className="h-6 w-6 text-[#1B2A4A]" />
              </div>
              <h3 className="text-2xl font-bold text-[#2D3748]">会社概要</h3>
            </div>

            <dl className="grid md:grid-cols-2 gap-x-8">
              {companyInfo.map((info, index) => (
                <div
                  key={index}
                  className="border-b border-[#E8E6E1] py-4 last:border-b-0"
                >
                  <dt className="text-xs font-bold tracking-widest text-[#7B8794] mb-1">
                    {info.label}
                  </dt>
                  <dd className="text-[#2D3748] font-semibold">
                    {info.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Company;
